// src/utils/lossRunTodo.js
//
// "What to do next" for the Loss Runs tab, derived from the coverage grid.
// Plain words, short. DoNotContact sources never appear. Pure.

import { authorizationState, authorizationNeed, boolOrNull, isLossRunEligible, policyState, dateOnlyOf, UNASSIGNED_ID } from './lossRunStatus';

const blank = v => v === null || v === undefined || String(v).trim() === '';

/**
 * buildTodo({ grid, sources, policies, requests, insured, today })
 *   policies / requests — this client's rows; used for the "n of N still
 *   needed" count, which is over EVERY eligible policy of the source so it
 *   matches the prompt's "Policies with no current loss run on file" list.
 * → [{ kind: 'auth'|'ask'|'find-contact'|'letter'|'done', text, sourceId?, lines?, terms?, needed?, total?, canPrompt }]
 */
export function buildTodo({ grid, sources = [], policies = [], requests = [], insured = {}, today, handled } = {}) {
  // handled: optional Map(policyId -> { handled, stillOpen, sentence, resolution })
  // from lossRunResolutions.resolutionIndex. A handled policy drops out of the
  // ask lines; a source whose open policies are all handled disappears.
  const items = [];
  if (!grid) return items;
  const byId = new Map((sources || []).map(s => [String(s.id), s]));
  const isHandledId = id => !!handled?.get?.(String(id))?.handled;
  // Needed = every policy of the source without a CURRENT run (stale copies
  // still count as needed); refreshes = how many of those are stale copies
  // already on file rather than never requested.
  const stillNeeded = (sourceId) => {
    const source = byId.get(sourceId) || null;
    const pols = (policies || []).filter(p => isLossRunEligible(p) && String(p.LossRunSourceId ?? '').trim() === sourceId);
    let needed = 0, refreshes = 0;
    for (const p of pols) {
      const st = policyState({ policy: p, source, requests, today });
      if (st.color === 'green') continue;
      if (isHandledId(p.id)) continue;                 // handled this renewal: not needed
      needed++;
      if (st.color === 'yellow' && dateOnlyOf(st.latestRequest?.DateReceived)) refreshes++;
    }
    return { needed, total: pols.length, refreshes };
  };
  const neededText = ({ needed, refreshes }) => {
    if (!needed) return '';
    if (refreshes === 0) return `: ${needed} needed`;
    if (refreshes === 1) return `: ${needed} needed, 1 is a refresh`;
    return `: ${needed} needed, ${refreshes} are refreshes of old copies`;
  };
  const auth = authorizationState(insured, today);
  const insuredName = String(insured?.Title || insured?.CompanyName || 'the insured').trim();

  const openRows = grid.rows.filter(r => r.need === 'required' || r.need === 'letter');

  // 1. Authorization letter — once, when any required / letter-accepted cell
  //    needs it (per policy, source override honoured) and no usable letter is on file.
  let needsLetter = false;
  for (const r of openRows) for (const term of grid.terms) {
    const cell = r.cells[term];
    if (!cell) continue;
    for (const chip of cell.chips) {
      if (chip.sourceId === UNASSIGNED_ID || chip.doNotContact) continue;
      const source = byId.get(chip.sourceId);
      const pols = cell.policies.filter(p => chip.policyIds.includes(String(p.id)));
      if (authorizationNeed(source, pols).need === 'needed') { needsLetter = true; break; }
    }
    if (needsLetter) break;
  }
  if (needsLetter && !auth.usable) {
    items.push({ kind: 'auth', text: `Get a signed loss run authorization from ${insuredName}. One letter covers every company.`, canPrompt: false });
  }

  // 2. One item per source with a non-green cell on a required line.
  const perSource = new Map();
  for (const r of grid.rows.filter(x => x.need === 'required')) for (const term of grid.terms) {
    const cell = r.cells[term];
    if (!cell) continue;
    for (const chip of cell.chips) {
      if (chip.color === 'green' || chip.sourceId === UNASSIGNED_ID || chip.doNotContact) continue;
      if (chip.check === 'all') continue;             // every non-green policy in this cell is handled
      if (!perSource.has(chip.sourceId)) perSource.set(chip.sourceId, { name: chip.name, lines: new Set(), terms: new Set() });
      const e = perSource.get(chip.sourceId);
      e.lines.add(r.lob); e.terms.add(term);
    }
  }
  for (const [sourceId, e] of [...perSource.entries()].sort((a, b) => a[1].name.localeCompare(b[1].name))) {
    const source = byId.get(sourceId);
    if (source && boolOrNull(source.DoNotContact) === true) continue;
    const lines = [...e.lines].sort();
    const terms = [...e.terms].sort();
    const { needed, total, refreshes } = policies.length ? stillNeeded(sourceId) : { needed: 0, total: 0, refreshes: 0 };
    if (policies.length && needed === 0) continue;   // everything still open for this source is handled
    const suffix = neededText({ needed, refreshes });
    if (source && !blank(source.ContactEmail)) {
      items.push({ kind: 'ask', sourceId, text: `Ask ${e.name} for ${lines.join(', ')} (${terms.join(', ')})${suffix}`, lines, terms, needed, total, refreshes, canPrompt: true });
    } else {
      items.push({ kind: 'find-contact', sourceId, text: `Find a loss run contact for ${e.name}${suffix}`, lines, terms, needed, total, refreshes, canPrompt: false });
    }
  }

  // 3. Letter-accepted lines that are not green.
  const letterOpen = grid.rows.some(r => r.need === 'letter' && grid.terms.some(t => r.cells[t] && r.cells[t].color !== 'green'));
  if (letterOpen) {
    items.push({ kind: 'letter', text: 'Excess/umbrella: a signed no known loss letter usually satisfies this. Confirm with the wholesaler first.', canPrompt: false });
  }

  // Still-open resolutions carry their note.
  const stillOpenNotes = [];
  for (const [, h] of handled || []) {
    if (h.stillOpen && h.resolution && !stillOpenNotes.some(x => x.res === h.resolution)) stillOpenNotes.push({ res: h.resolution, note: String(h.resolution.Notes ?? '').trim() });
  }
  if (stillOpenNotes.length) {
    items.push({ kind: 'still-open', text: `${stillOpenNotes.length} item${stillOpenNotes.length === 1 ? '' : 's'} marked still open: ${stillOpenNotes.map(x => x.note || '(no note)').join('; ')}`, canPrompt: false });
  }

  if (items.length === 0) items.push({ kind: 'done', text: 'Nothing to do. All required lines are current.', canPrompt: false });
  return items;
}
